import React from 'react';
import Select from 'react-select';

import { brandSelectTheme } from '../Common/reactSelectTheme';

/**
 * The one dropdown for the settings pages.
 *
 * Settings had native `<select className="modern-input">` in eleven places
 * while the rest of the app uses react-select — so the same act of choosing
 * something looked and behaved differently depending on which page you were
 * on, and none of the settings dropdowns could be typed into. This wraps
 * react-select with the brand theme and a size that matches `.modern-input`,
 * so a dropdown sitting next to a text field reads as the same control.
 *
 * Takes and returns the primitive value rather than react-select's
 * `{ value, label }` object, which is what every call site here actually has.
 * Anything react-select supports can still be passed straight through.
 */

// Height, radius and type all match .modern-input in _settings-sections.scss —
// these controls sit next to each other in the same form. Change them together.
// 0.875rem rather than a px number so it tracks the root size like the inputs
// do; as `fontSize: 14` the dropdown text was visibly larger than the text
// field beside it.
const SELECT_STYLES = {
  control: (base, state) => ({
    ...base,
    minHeight: 34,
    fontSize: '0.875rem',
    borderRadius: 6,
    backgroundColor: state.isDisabled ? '#f9fafb' : base.backgroundColor,
  }),
  valueContainer: (base) => ({ ...base, padding: '0 0.5rem' }),
  // react-select ships a 20px chevron; the settings icon scale is 16.
  dropdownIndicator: (base) => ({
    ...base,
    padding: 4,
    '& svg': { width: 16, height: 16 },
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: 4,
    '& svg': { width: 16, height: 16 },
  }),
  // The separator stays — owner preference, and ListToolbar keeps it too, so
  // the two react-select call sites at least agree on the indicator.
  indicatorSeparator: (base) => ({ ...base, marginTop: 8, marginBottom: 8 }),
  option: (base) => ({ ...base, fontSize: '0.875rem' }),
  // These sit inside cards that create their own stacking contexts, so the
  // menu is portalled to <body> and would otherwise be clipped.
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const portalTarget = typeof document !== 'undefined' ? document.body : null;

const SettingsSelect = ({
  id,
  options = [],
  value,
  onChange,
  placeholder = 'Välj…',
  ...rest
}) => (
  <Select
    inputId={id}
    options={options}
    value={options.find((o) => o.value === value) ?? null}
    onChange={(option) => onChange(option ? option.value : null)}
    theme={brandSelectTheme}
    styles={SELECT_STYLES}
    menuPortalTarget={portalTarget}
    placeholder={placeholder}
    {...rest}
  />
);

export default SettingsSelect;
